import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getEventsForPeriod, useSession } from '@ais-ui/websockets';
import { useEventNotification } from './useEventNotification';

type Severity = 'note' | 'warning' | 'alert';
export type BackendSeverity = 'WDESEV_NOTE' | 'WDESEV_WARNING' | 'WDESEV_ALERT';

export type Notification = {
  type: 'event';
  id: string;
  severity: Severity;
  mainText: string;
  secondaryText: string;
};

interface NotificationContextProps {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
}

const NotificationContext = createContext<NotificationContextProps | null>(
  null
);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      'useNotificationContext must be used within a NotificationProvider'
    );
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

interface EventDetail {
  event_ids: string[];
}

const translateEventSeverity = (severity: BackendSeverity): Severity => {
  if (severity === 'WDESEV_NOTE') {
    return 'note';
  }
  if (severity === 'WDESEV_WARNING') {
    return 'warning';
  }
  if (severity === 'WDESEV_ALERT') {
    return 'alert';
  }

  return 'note';
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { olSession } = useSession();

  // Function to update notifications
  const addNotification = (notification: Notification) => {
    setNotifications((prevNotifications) => [
      ...prevNotifications,
      notification,
    ]);
  };

  useEventNotification(olSession);

  useEffect(() => {
    const handleEventNotification = async (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;

      const endDateTime = new Date();
      const startDateTime = new Date(endDateTime.getTime() - 5 * 60 * 1000);

      const data = await getEventsForPeriod(
        olSession,
        startDateTime,
        endDateTime
      );

      for (const detectedEvent of data.events) {
        if (customEvent.detail.event_ids.includes(detectedEvent.id)) {
          const notification: Notification = {
            type: 'event',
            id: detectedEvent.id,
            severity: translateEventSeverity(
              detectedEvent.severity as BackendSeverity
            ),
            mainText: detectedEvent.name,
            secondaryText: 'event',
          };

          addNotification(notification);
        }
      }
    };

    window.addEventListener('eventNotification', handleEventNotification);
    return () => {
      window.removeEventListener('eventNotification', handleEventNotification);
    };
  }, [olSession]);

  const value = {
    notifications,
    setNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
